import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import AwaitingHumanWidget from "@/components/AwaitingHumanWidget";
import TodayAgendaWidget from "@/components/TodayAgendaWidget";
import AgentusHojeWidget from "@/components/AgentusHojeWidget";
import SetupWizard from "@/components/operacao/SetupWizard";
import { UsersThree, Car, Lightning, ChatCircleDots, CalendarCheck, CheckCircle, Circle, Sparkle, WhatsappLogo, Camera, X, Pulse, CurrencyDollar, ChatTeardropText, PencilSimple, Heartbeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import CreditsCard from "@/components/CreditsCard";
import CreditLowBanner from "@/components/CreditLowBanner";
import { useSector } from "@/lib/sectorContext";

// ---------- Animated number (count-up) ----------
function useAnimatedNumber(target, duration = 900) {
  const [val, setVal] = useState(target ?? 0);
  const fromRef = useRef(target ?? 0);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    if (from === to) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return val;
}

function AnimatedInt({ value, className = "" }) {
  const v = useAnimatedNumber(value);
  return <span className={`tabular-nums ${className}`}>{Math.round(v).toLocaleString("pt-BR")}</span>;
}

function AnimatedBRL({ valueCents, className = "" }) {
  const v = useAnimatedNumber(valueCents);
  const formatted = (Math.round(v) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
  return <span className={`tabular-nums ${className}`}>{formatted}</span>;
}

function PulseOnChange({ value, children, className = "" }) {
  const prev = useRef(value);
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (prev.current !== value && value > prev.current) {
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 1400);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);
  return (
    <span className={`inline-block transition-all duration-700 ${pulsing ? "text-emerald-600 scale-105" : ""} ${className}`}>
      {children}
    </span>
  );
}

function WhatsAppCaptureCard() {
  const [hidden, setHidden] = useState(() => localStorage.getItem("dismiss_wa_capture") === "1");
  // v0.98 · Fev/2026 — Este card é 100% automotivo (CRLV, BYD Song, placa).
  // Não faz sentido em Pet Shop, Salão, Clínica etc. Só renderiza em
  // tenants automotivos (workshop, dealer, carwash).
  const { isAutomotive } = useSector();
  if (hidden || !isAutomotive) return null;
  return (
    <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="wa-capture-card">
      <button
        onClick={() => { localStorage.setItem("dismiss_wa_capture", "1"); setHidden(true); }}
        className="absolute top-3 right-3 p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
        data-testid="wa-capture-dismiss"
        title="Não mostrar mais"
      >
        <X size={14} weight="regular" />
      </button>
      <div className="p-5 md:p-6 grid md:grid-cols-[auto_1fr] gap-5">
        <div className="hidden md:flex w-12 h-12 rounded-md border border-slate-200 items-center justify-center text-slate-700 shrink-0">
          <Camera size={20} weight="regular" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-[0.16em] font-medium">
            <WhatsappLogo size={12} weight="regular" /> Novidade
          </div>
          <h3 className="font-heading text-xl font-semibold text-slate-900 mt-1.5 tracking-tight">Cadastre veículos por WhatsApp em segundos</h3>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
            Seus clientes (ou você mesmo) podem cadastrar um veículo direto pelo WhatsApp da sua oficina — sem precisar abrir o sistema.
          </p>
          <ol className="mt-4 grid gap-2 text-sm text-slate-700">
            <Step n="1" text="Envie ao WhatsApp da oficina uma foto do CRLV (impresso ou do gov.br) — ou os dados em texto." />
            <Step n="2" text="Se o documento tiver QR Code, o AgentUS lê ele instantaneamente (placa, chassi e Renavam)." />
            <Step n="3" text='Confirme com "SIM" e pronto — o veículo é cadastrado em segundos.' />
          </ol>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Dica: o CRLV-e do gov.br tem QR Code oficial Senatran, que é a forma mais rápida e segura. Texto livre (ex: <span className="font-mono text-[11px] text-slate-700">ABC1D23, BYD Song Pro 2024, Preto</span>) também funciona.
          </p>
        </div>
      </div>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-5 h-5 rounded-full border border-slate-300 text-slate-700 text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5 tabular-nums">{n}</span>
      <span>{text}</span>
    </li>
  );
}

function fmtBRL(cents) {
  const v = (cents || 0) / 100;
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

function AiImpactKpi({ icon: Icon, label, valueNode, sub, pulseKey }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-colors" data-testid={`ai-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</div>
        <Icon size={16} weight="regular" className="text-slate-400" />
      </div>
      <div className="font-heading text-[34px] font-semibold text-slate-900 tracking-tight leading-none mt-3">
        <PulseOnChange value={pulseKey ?? 0}>{valueNode}</PulseOnChange>
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1.5 tracking-tight">{sub}</div>}
    </div>
  );
}

function FunnelBar({ label, value, max, hint }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div data-testid={`funnel-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</span>
        <span className="text-sm text-slate-900 tabular-nums font-medium">
          {value}{hint && <span className="text-slate-400 ml-2 text-xs">{hint}</span>}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-slate-900 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniChart({ series }) {
  const maxDec = Math.max(1, ...series.map((s) => s.decisions));
  const maxAppt = Math.max(1, ...series.map((s) => s.appointments));
  const maxAll = Math.max(maxDec, maxAppt);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Atividade dos últimos 7 dias</h4>
          <p className="text-[11px] text-slate-500">Decisões executadas vs. agendamentos gerados</p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-900" />Decisões</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Agendamentos</span>
        </div>
      </div>
      <div className="flex items-end justify-between gap-1 h-28">
        {series.map((s, i) => {
          const decH = (s.decisions / maxAll) * 100;
          const apptH = (s.appointments / maxAll) * 100;
          const day = new Date(s.date + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "short" }).slice(0, 3);
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`chart-day-${i}`}>
              <div className="w-full flex items-end justify-center gap-0.5 h-24">
                <div className="w-1/2 bg-slate-900 rounded-t-sm transition-all duration-500" style={{ height: `${decH}%`, minHeight: s.decisions ? 2 : 0 }} title={`${s.decisions} decisões`} />
                <div className="w-1/2 bg-emerald-500 rounded-t-sm transition-all duration-500" style={{ height: `${apptH}%`, minHeight: s.appointments ? 2 : 0 }} title={`${s.appointments} agendamentos`} />
              </div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">{day}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TicketEditor({ ticketCents, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(Math.round((ticketCents || 0) / 100)));
  const save = async () => {
    const cents = Math.max(0, Math.round(parseFloat(val.replace(",", ".")) * 100)) || 0;
    try {
      await api.put("/dashboard/ai_settings", { ai_ticket_medio_cents: cents });
      toast.success("Ticket médio atualizado");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error("Falha ao salvar");
    }
  };
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid="ticket-edit-btn"
        className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-900 transition-colors"
      >
        <PencilSimple size={11} weight="regular" />
        Ajustar ticket médio (atual: {fmtBRL(ticketCents)})
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="text-slate-500">R$</span>
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid="ticket-input"
        autoFocus
        className="w-20 px-2 py-1 border border-slate-300 rounded text-slate-900 tabular-nums focus:outline-none focus:border-slate-900"
      />
      <button onClick={save} data-testid="ticket-save-btn" className="px-2.5 py-1 bg-slate-900 text-white rounded hover:bg-slate-700 transition-colors">Salvar</button>
      <button onClick={() => setEditing(false)} className="text-slate-500 hover:text-slate-900">Cancelar</button>
    </div>
  );
}

function AiImpactPanel({ impact, onReload, live }) {
  if (!impact) return null;
  const k = impact.kpis || {};
  const f = impact.funnel || {};
  const totalConv = k.ai_conversations_started || 0;
  const totalAppt = k.ai_appointments || 0;
  const revenue = k.estimated_revenue_cents || 0;
  const max = Math.max(f.decisions_executed || 0, f.replies_received || 0, f.appointments || 0, 1);

  return (
    <section className="space-y-4" data-testid="ai-impact-panel">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-[0.18em] font-medium">
            <Pulse size={12} weight="regular" /> Impacto do AgentUS · últimos 7 dias
          </div>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight mt-1">
            O AgentUS iniciou{" "}
            <PulseOnChange value={totalConv}>
              <AnimatedInt value={totalConv} className="text-slate-900" />
            </PulseOnChange>
            {" "}conversa{totalConv === 1 ? "" : "s"} e gerou{" "}
            <PulseOnChange value={totalAppt}>
              <AnimatedInt value={totalAppt} className="text-slate-900" />
            </PulseOnChange>
            {" "}agendamento{totalAppt === 1 ? "" : "s"} — receita estimada{" "}
            <PulseOnChange value={revenue}>
              <AnimatedBRL valueCents={revenue} className="text-slate-900" />
            </PulseOnChange>
            .
          </h2>
        </div>
        {live && (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 shrink-0" data-testid="ai-impact-live-indicator" title="Atualizando automaticamente a cada 30s">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Ao vivo
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <AiImpactKpi
          icon={UsersThree}
          label="Clientes reativados"
          pulseKey={k.customers_reactivated ?? 0}
          valueNode={<AnimatedInt value={k.customers_reactivated ?? 0} />}
          sub="únicos no período"
        />
        <AiImpactKpi
          icon={ChatCircleDots}
          label="Conversas iniciadas pelo AgentUS"
          pulseKey={k.ai_conversations_started ?? 0}
          valueNode={<AnimatedInt value={k.ai_conversations_started ?? 0} />}
          sub={f.replies_received ? `${f.replies_received} resposta${f.replies_received === 1 ? "" : "s"} recebida${f.replies_received === 1 ? "" : "s"}` : "aguardando respostas"}
        />
        <AiImpactKpi
          icon={CalendarCheck}
          label="Agendamentos automáticos"
          pulseKey={k.ai_appointments ?? 0}
          valueNode={<AnimatedInt value={k.ai_appointments ?? 0} />}
          sub={f.conversion_rate ? `taxa de conversão ${(f.conversion_rate * 100).toFixed(1)}%` : "—"}
        />
        <AiImpactKpi
          icon={CurrencyDollar}
          label="Receita estimada"
          pulseKey={revenue}
          valueNode={<AnimatedBRL valueCents={revenue} />}
          sub={`${totalAppt} × ticket médio`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Funil de conversão</h4>
              <p className="text-[11px] text-slate-500">Como cada decisão se transforma em receita</p>
            </div>
            <Link to="/app/decisao" className="text-[11px] text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-wider">Ver decisões →</Link>
          </div>
          <div className="space-y-4">
            <FunnelBar label="Decisões executadas" value={f.decisions_executed || 0} max={max} />
            <FunnelBar label="Respostas recebidas" value={f.replies_received || 0} max={max} hint={f.reply_rate ? `${(f.reply_rate * 100).toFixed(1)}%` : ""} />
            <FunnelBar label="Agendamentos gerados" value={f.appointments || 0} max={max} hint={f.conversion_rate ? `${(f.conversion_rate * 100).toFixed(1)}%` : ""} />
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100">
            <TicketEditor ticketCents={impact.ticket_medio_cents} onSaved={onReload} />
          </div>
        </div>
        <MiniChart series={impact.series || []} />
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</div>
        <Icon size={16} weight="regular" className="text-slate-400" />
      </div>
      <div className="font-heading text-[34px] font-semibold text-slate-900 tracking-tight leading-none mt-3 tabular-nums">{value}</div>
    </div>
  );
}

// ── Onboarding card REMOVED (Feb/2026) ─────────────────────────────────────
// Was a slate-900 dark card listing 6 generic milestones (workspace,
// import, rules, first_conversation, first_appointment, whatsapp_real).
// Replaced by the SetupWizard + AI Impact panel combo, which surface
// the same signal in a coherent visual language. The backend still
// returns `onboarding` inside /api/dashboard/summary for backwards
// compatibility — no consumers besides this defunct card.

// Lightweight in-memory cache so navigating away and back to Dashboard
// reuses the previous payload instantly instead of showing blank for ~600-1500ms
// while the API responds. The 30s polling effect still refreshes in the background.
const dashboardCache = { summary: null, impact: null, fetchedAt: 0 };

export default function Dashboard() {
  const [data, setData] = useState(dashboardCache.summary);
  const [impact, setImpact] = useState(dashboardCache.impact);
  const [running, setRunning] = useState(false);
  const { isClinic, labels, enablesAssetTab } = useSector();

  const load = async () => {
    const [{ data: summary }, { data: imp }] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/dashboard/ai_impact?days=7"),
    ]);
    dashboardCache.summary = summary;
    dashboardCache.impact = imp;
    dashboardCache.fetchedAt = Date.now();
    setData(summary);
    setImpact(imp);
  };

  // Lightweight polling: refresh AI impact only (cheap) every 30s while tab visible.
  const refreshImpact = async () => {
    try {
      const { data: imp } = await api.get("/dashboard/ai_impact?days=7");
      dashboardCache.impact = imp;
      setImpact(imp);
    } catch (e) { /* silent — keep last value */ }
  };

  useEffect(() => {
    // If cache is fresh (<60s), skip the loading round-trip and rehydrate
    // synchronously, then refresh in background.
    const fresh = Date.now() - dashboardCache.fetchedAt < 60_000;
    if (fresh && dashboardCache.summary) {
      setData(dashboardCache.summary);
      setImpact(dashboardCache.impact);
      refreshImpact();
    } else {
      load();
    }
  }, []);
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refreshImpact();
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const runEngine = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/events/run");
      if (r.created > 0) {
        toast.success(`${r.created} novo${r.created === 1 ? "" : "s"} evento${r.created === 1 ? "" : "s"} detectado${r.created === 1 ? "" : "s"}.`);
      } else {
        toast.success("Dashboard atualizado. Nenhuma nova ação necessária agora.");
      }
      await load();
    } catch (e) {
      toast.error("Não foi possível atualizar agora.");
    } finally {
      setRunning(false);
    }
  };

  const kpis = data?.kpis || {};
  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle="Visão operacional dos seus agentes."
        actions={
          <button
            onClick={runEngine}
            disabled={running}
            data-testid="dashboard-run-engine-btn"
            title="Verifica oportunidades de agir agora (em vez de esperar o ciclo automático)"
            className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-60"
          >
            <Lightning size={13} weight="regular" />
            {running ? "Atualizando…" : "Atualizar agora"}
          </button>
        }
      />
      <div className="p-6 md:p-8 space-y-6">
        <CreditLowBanner />
        <SetupWizard autoFadeOnComplete={true} />
        <AiImpactPanel impact={impact} onReload={load} live />
        <AwaitingHumanWidget />
        <AgentusHojeWidget />
        <TodayAgendaWidget />
        <WhatsAppCaptureCard />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi icon={UsersThree} label="Clientes" value={kpis.customers ?? "—"} />
          {enablesAssetTab && (
            <Kpi
              icon={isClinic ? Heartbeat : Car}
              label={labels.assetPlural(true)}
              value={kpis.assets ?? "—"}
            />
          )}
          <Kpi icon={Lightning} label="Eventos pendentes" value={kpis.events_pending ?? "—"} />
          <Kpi icon={ChatCircleDots} label="Conversas abertas" value={kpis.conversations_open ?? "—"} />
          <Kpi icon={CalendarCheck} label="Agendamentos" value={kpis.appointments_upcoming ?? "—"} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CreditsCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white border border-slate-200 rounded-md">
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold tracking-tight">Eventos recentes</h3>
              <Link to="/app/eventos" className="text-[12px] text-slate-500 hover:text-slate-900 font-medium transition-colors">Ver todos →</Link>
            </header>
            <ul className="divide-y divide-slate-100">
              {(data?.recent_events || []).length === 0 && (
                <li className="p-6 text-sm text-slate-500">
                  <div className="text-slate-700 font-medium mb-1">Tudo calmo por enquanto.</div>
                  <div className="text-xs leading-relaxed">Assim que o AgentUS detectar algo a fazer — uma manutenção atrasada, um cliente parado há muito tempo — vai aparecer aqui automaticamente.</div>
                </li>
              )}
              {(data?.recent_events || []).map((e) => (
                <li key={e.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-slate-900">{e.kind.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-500">{e.payload?.label || e.payload?.plate || "—"}</div>
                  </div>
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${
                    e.status === "pending" ? "bg-amber-50 text-amber-700" :
                    e.status === "scheduled" ? "bg-emerald-50 text-emerald-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{e.status}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white border border-slate-200 rounded-md">
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold tracking-tight">Próximos agendamentos</h3>
              <Link to="/app/agendamentos" className="text-[12px] text-slate-500 hover:text-slate-900 font-medium transition-colors">Ver todos →</Link>
            </header>
            <ul className="divide-y divide-slate-100">
              {(data?.upcoming_appointments || []).length === 0 && (
                <li className="p-6 text-sm text-slate-500">
                  <div className="text-slate-700 font-medium mb-1">Nenhum agendamento nos próximos dias.</div>
                  <div className="text-xs leading-relaxed">Quando o AgentUS — ou você — fechar um agendamento pelo WhatsApp, ele aparece aqui.</div>
                </li>
              )}
              {(data?.upcoming_appointments || []).map((a) => (
                <li key={a.id} className="p-4 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{a.service_code}</div>
                    <div className="text-xs text-slate-500">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${
                    a.status === "confirmed" ? "bg-emerald-50 text-emerald-700" :
                    a.status === "proposed" ? "bg-blue-50 text-blue-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{a.status}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
